import { type Component, Show, For, Switch, Match, createSignal } from "solid-js";
import { TbAlertTriangle, TbApps, TbAt, TbFileText, TbLoader2, TbStack, TbX } from "solid-icons/tb";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { ScopeManagerPane } from "./ScopeManagerPane/ScopeManagerPane";
import { useWire } from "~/wire";
import { lifecycleStateGrouped } from "~/components/utils/lifecycleStateMappers";
import { twMerge } from "tailwind-merge";
import { truncateMiddle } from "~/utils/truncateMiddle";
import { Dynamic } from "solid-js/web";
import { getIconComponent } from "@storytell/bsky-icons";
import { isLightColor } from "~/components/layouts/minimal/isLightColor";
import { LabelTooltip } from "~/components/labels/LabelTooltip";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import type { Asset, AssetId, Concept, ConceptId, Label, LabelId, View, ViewId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { dispatchGlobalEvent } from "@storytell/events";
import { Popover } from "@kobalte/core/popover";

const truncateFilename = (filename: string, maxLength = 25) => {
  if (filename.length <= maxLength) return filename;

  const extension = filename.split(".").pop();
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf("."));
  const truncatedName = nameWithoutExt.substring(0, maxLength - extension!.length - 4);

  return `${truncatedName}...${extension}`;
};
const truncateConceptName = (name: string, maxLength = 25) => {
  if (name.length <= maxLength) return name;
  return `${name.substring(0, maxLength)}...`;
};

export const ScopeDisplay: Component = () => {
  const {
    sync: s,
    chat: { scope },
    knowledgeUi,
  } = useWire();
  const assets = () =>
    scope
      .assets()
      .map((a) => s.assets.get(a as AssetId))
      .filter((a) => a !== undefined) as Asset[];
  const concepts = () =>
    scope
      .concepts()
      .map((c) => s.concepts.get(c as ConceptId))
      .filter((c) => c !== undefined) as Concept[];
  const labels = () =>
    scope
      .labels()
      .map((l) => s.labels.get(l as LabelId))
      .filter((l) => l !== undefined) as Label[];
  const views = () =>
    scope
      .views()
      .map((v) => s.views.get(v as ViewId))
      .filter((v) => v !== undefined) as View[];

  const totalItems = () => assets().length + concepts().length + labels().length + views().length;

  return (
    <div class="flex items-center gap-2 text-xs px-3 mb-2 text-secondary-text">
      <Show
        when={s.assets.searchIds("").length}
        fallback={
          <StPopover
            placement="top"
            content={
              <div class="flex flex-col gap-2 p-4 justify-center items-center">
                <p>Upload files to chat with</p>
                <Popover.CloseButton as={StButton} onClick={() => dispatchGlobalEvent("open-file-picker", undefined)}>
                  Upload Files
                </Popover.CloseButton>
              </div>
            }
          >
            <StPopoverTrigger
              as="button"
              type="button"
              class={twMerge(
                "group/btn shrink-0 flex bg-canvas dark:bg-tile/50 dark:hover:bg-tile-hover/50 hover:bg-canvas-hover rounded-full border border-muted-border hover:border-primary-border dark:border-white/10 dark:hover:border-white/10 items-center gap-1 py-1 px-2 cursor-pointer select-none",
                scope.allKnowledge() && "bg-brand hover:bg-brand-hover text-on-brand",
              )}
            >
              <TbAt
                class={twMerge(
                  "size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text",
                  scope.allKnowledge() && "text-on-brand opacity-80",
                )}
              />
              <span>Chat with</span>
            </StPopoverTrigger>
          </StPopover>
        }
      >
        <span
          tabIndex={0}
          role="button"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              knowledgeUi.open();
            }
          }}
          onClick={() => knowledgeUi.open()}
          class={twMerge(
            "group/btn shrink-0 flex bg-canvas dark:bg-tile/50 dark:hover:bg-tile-hover/50 hover:bg-canvas-hover rounded-full border border-muted-border hover:border-primary-border dark:border-white/10 dark:hover:border-white/10 items-center gap-1 py-1 px-2 cursor-pointer select-none",
            scope.allKnowledge() && "bg-brand hover:bg-brand-hover text-on-brand",
          )}
        >
          <TbAt
            class={twMerge(
              "size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text",
              scope.allKnowledge() && "text-on-brand opacity-80",
            )}
          />
          <span>
            {scope.allKnowledge() ? "Chatting with All Knowledge" : totalItems() === 0 ? "Chat with" : "Chatting with"}
          </span>

          <Show when={scope.allKnowledge()}>
            <button
              type="button"
              title="Remove all knowledge from scope"
              aria-label="Remove all knowledge from scope"
              onClick={(e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                scope.setAllKnowledge(false);
              }}
            >
              <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
            </button>
          </Show>
        </span>
      </Show>

      <Show when={!scope.allKnowledge()}>
        <div class="flex items-center gap-2 flex-auto overflow-x-auto scrollbar-hide">
          <For each={views()}>
            {(view) => (
              <span class="group/btn shrink-0 flex bg-canvas hover:bg-canvas-hover border border-primary-border rounded-full items-center gap-1 py-1 px-2 select-none">
                <TbStack class="size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-secondary-text" />
                <span>{truncateConceptName(view.name)}</span>

                <button
                  type="button"
                  title={`Remove ${view.name} from scope`}
                  aria-label={`Remove ${view.name} from scope`}
                  onClick={(e) => {
                    e.preventDefault();
                    scope.removeView(view.id);
                  }}
                >
                  <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
                </button>
              </span>
            )}
          </For>

          <For each={labels()}>
            {(label) => (
              <StTooltip
                placement="top"
                contentProps={{
                  class: "z-50",
                }}
                content={<LabelTooltip labelId={label.id} />}
              >
                <StTooltipTrigger
                  as="span"
                  class="group/btn shrink-0 flex bg-canvas hover:bg-canvas-hover border border-primary-border rounded-full items-center gap-1 py-1 px-2 select-none"
                >
                  <span
                    class="rounded-full size-4 grid place-content-center bg-[var(--label-color)]"
                    style={{
                      "--label-color": label.color,
                      "--label-icon": isLightColor(label.color || "") ? "black" : "white",
                    }}
                  >
                    <Dynamic
                      component={getIconComponent(label.icon)}
                      class="text-[var(--label-icon)] group-hover/btn:scale-hover group-active/btn:scale-active transition-transform"
                    />
                  </span>
                  <span>{truncateMiddle(label.name)}</span>

                  <button
                    type="button"
                    title={`Remove ${label.name} from scope`}
                    aria-label={`Remove ${label.name} from scope`}
                    onClick={(e) => {
                      e.preventDefault();
                      scope.removeLabel(label.id);
                    }}
                  >
                    <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
                  </button>
                </StTooltipTrigger>
              </StTooltip>
            )}
          </For>
          <For each={concepts()}>
            {(concept) => (
              <span class="group/btn shrink-0 flex bg-concept-bg text-concept-text hover:bg-concept-bg-hover border border-concept-border rounded-full items-center gap-1 py-1 px-2 select-none">
                <TbApps class="size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-concept-icon" />
                <span>{truncateConceptName(concept.name)}</span>

                <button
                  type="button"
                  title={`Remove ${concept.name} from scope`}
                  aria-label={`Remove ${concept.name} from scope`}
                  onClick={(e) => {
                    e.preventDefault();
                    scope.removeConcept(concept.id);
                  }}
                >
                  <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
                </button>
              </span>
            )}
          </For>

          <For each={scope.uploadingAssets()}>
            {(asset) => (
              <span class="group/btn shrink-0 flex bg-asset-bg hover:bg-asset-bg-hover text-asset-text border border-asset-border rounded-full items-center gap-1 py-1 px-2 select-none">
                <Switch>
                  <Match when={asset.status === "uploading"}>
                    <TbLoader2 class="w-3 h-3 animate-spin group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-asset-icon" />
                  </Match>
                  <Match when={asset.status === "error"}>
                    <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform" />
                  </Match>
                </Switch>
                <span class="text-xs">{truncateFilename(asset.filename)}</span>

                <button
                  type="button"
                  title={`Remove ${asset.filename} from scope`}
                  aria-label={`Remove ${asset.filename} from scope`}
                  onClick={(e) => {
                    e.preventDefault();
                    scope.removeAsset(asset.id);
                  }}
                >
                  <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
                </button>
              </span>
            )}
          </For>

          <For each={assets()}>
            {(asset) => (
              <span class="group/btn shrink-0 flex bg-asset-bg hover:bg-asset-bg-hover text-asset-text border border-asset-border rounded-full items-center gap-1 py-1 px-2 select-none">
                <Switch>
                  <Match when={["uploading", "processing"].includes(lifecycleStateGrouped(asset.lifecycleState))}>
                    <TbLoader2 class="w-3 h-3 animate-spin group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-asset-icon" />
                  </Match>
                  <Match when={lifecycleStateGrouped(asset.lifecycleState) === "error"}>
                    <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform" />
                  </Match>
                  <Match when={lifecycleStateGrouped(asset.lifecycleState) === "ready"}>
                    <TbFileText class="w-3 h-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-asset-icon" />
                  </Match>
                </Switch>
                <span>{truncateFilename(asset.fileName)}</span>

                <button
                  type="button"
                  title={`Remove ${asset.fileName} from scope`}
                  aria-label={`Remove ${asset.fileName} from scope`}
                  onClick={(e) => {
                    e.preventDefault();
                    scope.removeAsset(asset.id);
                  }}
                >
                  <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
                </button>
              </span>
            )}
          </For>
        </div>
        <Show when={totalItems() > 1}>
          <button
            type="button"
            onClick={scope.clearAll}
            class="text-xs shrink-0 text-muted-text hover:text-secondary-text transition-colors underline cursor-pointer select-none"
            title="Clear all assets from scope"
          >
            Clear all
          </button>
        </Show>
      </Show>
    </div>
  );
};
